// SourceSlot — one uploaded artifact + a semantic tag telling ingest what role
// it plays. Replaces the old single-PDF `CourseDraftSource` + dedicated
// `PastPaperUpload[]` model (Session 19AD).
//
// Why slotted:
//   - Different ingest stages care about different sources. extract_cases wants
//     textbook / lecture_notes / case_study. parse_past_papers wants past_paper.
//     lecture_notes stage prefers lecture_notes kind. Tagging at upload time
//     lets each stage pull only what it needs without keyword-guessing.
//   - Multi-file workflow: drag in a folder of mixed assets, pick kinds, go.
//
// Bytes live in IDB `pdfs` store (same store as before — keyed by sha256). This
// schema only carries metadata; format-specific parsing happens in the browser
// via @examforge/parser before bytes are sent to the worker.
import { z } from 'zod';
export const SourceSlotKindSchema = z.enum([
    'textbook', // chapters / definitions — primary signal for extract_cases
    'lecture_notes', // structured slide / class notes — lecture_notes stage prefers
    'past_paper', // past exam papers — parse_past_papers consumes; mock_papers may ref
    'syllabus', // course outline — hints chapter spine for extract_cases
    'case_study', // primary readings (business / law / med) — extract_cases consumes
    'reading', // supplementary article / journal paper
    'exercise', // practice problem sets / exercise collections
    'rubric', // grading rubric / marking scheme / answer keys
    'assignment', // homework / coursework assignments
    'custom', // user-described (customKindNote required)
]);
export const ALL_SOURCE_SLOT_KINDS = [
    'textbook', 'lecture_notes', 'past_paper', 'syllabus', 'case_study', 'reading',
    'exercise', 'rubric', 'assignment', 'custom',
];
export const SourceSlotFormatSchema = z.enum(['pdf', 'pptx', 'docx', 'md', 'txt']);
export const SourceSlotSchema = z.object({
    /** Slug. New: `src_<sha8>`. Legacy past papers retain their `past_<sha8>` id
     *  via migration so MockPaperSpec.pastPaperRef back-references survive. */
    id: z.string().regex(/^[a-z][a-z0-9_-]{0,63}$/),
    kind: SourceSlotKindSchema,
    format: SourceSlotFormatSchema,
    filename: z.string().min(1).max(255),
    /** Lowercase hex sha256 of file bytes; IDB pdfs store key. */
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    byteLength: z.number().int().nonnegative(),
    /** User-entered label (e.g. "2024 Final", "Chapter 3"). Optional. */
    label: z.string().min(1).max(120).optional(),
    /** Year — only meaningful for past_paper; drives "default to most recent" UI. */
    year: z.number().int().min(1990).max(2100).optional(),
    /** Priority hint for the LLM. 1 = highest signal, 10 = lowest. */
    priority: z.number().int().min(1).max(10).optional(),
    /** Free-text description when kind === 'custom'. */
    customKindNote: z.string().min(1).max(120).optional(),
});
/** Filter slots by kind. */
export function slotsByKind(slots, kind) {
    return slots.filter((s) => s.kind === kind);
}
/** Auto-guess kind from filename. UI uses this as the default; user can override. */
export function guessSlotKind(filename) {
    const lower = filename.toLowerCase();
    if (/past[-_ ]?paper|exam[-_ ]?\d{2,4}|final[-_ ]?\d{2,4}|mid[-_ ]?term|resit/.test(lower)) {
        return 'past_paper';
    }
    if (/syllabus|outline|course[-_ ]?info/.test(lower))
        return 'syllabus';
    if (/lecture|slide|deck|class[-_ ]?\d|week[-_ ]?\d|topic[-_ ]?\d/.test(lower)) {
        return 'lecture_notes';
    }
    if (/case[-_ ]?study|harvard|hbr/.test(lower))
        return 'case_study';
    if (/exercise|problem[-_ ]?set|practice[-_ ]?question|drill|习题|练习/.test(lower))
        return 'exercise';
    if (/rubric|marking[-_ ]?scheme|grade[-_ ]?(descriptor|criteria)|answer[-_ ]?key|评分|答案/.test(lower))
        return 'rubric';
    if (/assignment|homework|coursework|作业|hw\d/i.test(lower))
        return 'assignment';
    if (/textbook|chapter|ch[-_ ]?\d|book/.test(lower))
        return 'textbook';
    if (/reading|article|paper(?!s)|journal/.test(lower))
        return 'reading';
    return 'textbook';
}
/** Detect format from filename. Mirrors @examforge/parser dispatch. */
export function detectSlotFormat(filename) {
    const lower = filename.toLowerCase();
    if (lower.endsWith('.pdf'))
        return 'pdf';
    if (lower.endsWith('.pptx'))
        return 'pptx';
    if (lower.endsWith('.docx'))
        return 'docx';
    if (lower.endsWith('.md') || lower.endsWith('.markdown'))
        return 'md';
    return 'txt';
}
//# sourceMappingURL=source-slot.js.map